import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API from "../util/API";

const normalizeId = (value) => String(value ?? "").trim();

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const normalizeName = (actor, fallback = "User") => {
  if (!actor || typeof actor !== "object") return fallback;

  const direct = normalizeId(actor?.name);
  if (direct) return direct;

  const full = [normalizeId(actor?.firstName), normalizeId(actor?.lastName)]
    .filter(Boolean)
    .join(" ")
    .trim();
  if (full) return full;

  const username = normalizeId(actor?.username);
  if (username) return username;

  return fallback;
};

const normalizeQnaItem = (item) => {
  const likesCount = Number.isFinite(Number(item?.likesCount))
    ? Number(item.likesCount)
    : Array.isArray(item?.likes)
    ? item.likes.length
    : 0;

  const dislikesCount = Number.isFinite(Number(item?.dislikesCount))
    ? Number(item.dislikesCount)
    : Array.isArray(item?.dislikes)
    ? item.dislikes.length
    : 0;

  return {
    ...item,
    productId: normalizeId(item?.productId || item?.product?.productId),
    user: item?.user
      ? {
          ...item.user,
          name: normalizeName(item.user, "User"),
        }
      : null,
    answeredBy: item?.answeredBy
      ? {
          ...item.answeredBy,
          name: normalizeName(
            item.answeredBy,
            item?.answeredBy?.type === "admin" ? "Admin" : "User"
          ),
        }
      : null,
    likesCount,
    dislikesCount,
    helpfulCount: Number.isFinite(Number(item?.helpfulCount))
      ? Number(item.helpfulCount)
      : likesCount,
  };
};

const initialState = {
  listByProduct: {},
  listStatusByProduct: {},
  listErrorByProduct: {},
  askStatusByProduct: {},
  askErrorByProduct: {},
  reactionStatusByQuestionId: {},
  reactionErrorByQuestionId: {},
};

export const fetchQnaByProduct = createAsyncThunk(
  "productQna/fetchQnaByProduct",
  async (productRef, thunkAPI) => {
    const productKey = normalizeId(productRef);
    if (!productKey) {
      return thunkAPI.rejectWithValue("Product id is required");
    }

    try {
      const response = await API.get(`/qna/product/${encodeURIComponent(productKey)}`);
      const list = Array.isArray(response?.data?.data) ? response.data.data : [];

      return {
        productKey,
        items: list.map((item) => normalizeQnaItem(item)),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to load questions"));
    }
  }
);

export const askProductQuestion = createAsyncThunk(
  "productQna/askProductQuestion",
  async ({ productRef, question }, thunkAPI) => {
    const productKey = normalizeId(productRef);
    const questionText = normalizeId(question);

    if (!productKey) {
      return thunkAPI.rejectWithValue("Product id is required");
    }

    if (!questionText) {
      return thunkAPI.rejectWithValue("Question is required");
    }

    try {
      const response = await API.post("/qna", {
        product: productKey,
        question: questionText,
      });

      return {
        productKey,
        item: normalizeQnaItem(response?.data?.data || {}),
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to submit question"));
    }
  }
);

export const likeProductQuestion = createAsyncThunk(
  "productQna/likeProductQuestion",
  async ({ questionId }, thunkAPI) => {
    const qnaId = normalizeId(questionId);
    if (!qnaId) {
      return thunkAPI.rejectWithValue("Question id is required");
    }

    try {
      const response = await API.put(`/qna/${encodeURIComponent(qnaId)}/like`, {});
      return {
        qnaId,
        ...response?.data?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to like question"));
    }
  }
);

export const dislikeProductQuestion = createAsyncThunk(
  "productQna/dislikeProductQuestion",
  async ({ questionId }, thunkAPI) => {
    const qnaId = normalizeId(questionId);
    if (!qnaId) {
      return thunkAPI.rejectWithValue("Question id is required");
    }

    try {
      const response = await API.put(`/qna/${encodeURIComponent(qnaId)}/dislike`, {});
      return {
        qnaId,
        ...response?.data?.data,
      };
    } catch (error) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to dislike question"));
    }
  }
);

const productQnaSlice = createSlice({
  name: "productQna",
  initialState,
  reducers: {
    clearQnaErrors(state, action) {
      const productKey = normalizeId(action.payload);
      if (!productKey) return;
      state.listErrorByProduct[productKey] = "";
      state.askErrorByProduct[productKey] = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQnaByProduct.pending, (state, action) => {
        const productKey = normalizeId(action.meta.arg);
        if (!productKey) return;
        state.listStatusByProduct[productKey] = "loading";
        state.listErrorByProduct[productKey] = "";
      })
      .addCase(fetchQnaByProduct.fulfilled, (state, action) => {
        const { productKey, items } = action.payload;
        state.listStatusByProduct[productKey] = "succeeded";
        state.listErrorByProduct[productKey] = "";
        state.listByProduct[productKey] = items;
      })
      .addCase(fetchQnaByProduct.rejected, (state, action) => {
        const productKey = normalizeId(action.meta.arg);
        if (!productKey) return;
        state.listStatusByProduct[productKey] = "failed";
        state.listErrorByProduct[productKey] =
          action.payload || action.error?.message || "Failed to load questions";
      })
      .addCase(askProductQuestion.pending, (state, action) => {
        const productKey = normalizeId(action.meta.arg?.productRef);
        if (!productKey) return;
        state.askStatusByProduct[productKey] = "loading";
        state.askErrorByProduct[productKey] = "";
      })
      .addCase(askProductQuestion.fulfilled, (state, action) => {
        const { productKey, item } = action.payload;
        state.askStatusByProduct[productKey] = "succeeded";
        state.askErrorByProduct[productKey] = "";

        const existing = Array.isArray(state.listByProduct[productKey])
          ? state.listByProduct[productKey]
          : [];

        state.listByProduct[productKey] = [
          item,
          ...existing.filter((qna) => normalizeId(qna?._id) !== normalizeId(item?._id)),
        ];
      })
      .addCase(askProductQuestion.rejected, (state, action) => {
        const productKey = normalizeId(action.meta.arg?.productRef);
        if (!productKey) return;
        state.askStatusByProduct[productKey] = "failed";
        state.askErrorByProduct[productKey] =
          action.payload || action.error?.message || "Failed to submit question";
      })
      .addCase(likeProductQuestion.pending, (state, action) => {
        const qnaId = normalizeId(action.meta.arg?.questionId);
        if (!qnaId) return;
        state.reactionStatusByQuestionId[qnaId] = "loading";
        state.reactionErrorByQuestionId[qnaId] = "";
      })
      .addCase(likeProductQuestion.fulfilled, (state, action) => {
        const qnaId = normalizeId(action.payload?.qnaId || action.payload?._id || action.meta.arg?.questionId);
        if (!qnaId) return;

        state.reactionStatusByQuestionId[qnaId] = "succeeded";
        state.reactionErrorByQuestionId[qnaId] = "";

        const likes = Number.isFinite(Number(action.payload?.likes))
          ? Number(action.payload.likes)
          : null;
        const dislikes = Number.isFinite(Number(action.payload?.dislikes))
          ? Number(action.payload.dislikes)
          : null;
        const helpfulCount = Number.isFinite(Number(action.payload?.helpfulCount))
          ? Number(action.payload.helpfulCount)
          : likes;

        Object.keys(state.listByProduct).forEach((productKey) => {
          state.listByProduct[productKey] = (state.listByProduct[productKey] || []).map((item) => {
            if (normalizeId(item?._id) !== qnaId) return item;
            return {
              ...item,
              likesCount: likes ?? item.likesCount ?? 0,
              dislikesCount: dislikes ?? item.dislikesCount ?? 0,
              helpfulCount: helpfulCount ?? item.helpfulCount ?? item.likesCount ?? 0,
            };
          });
        });
      })
      .addCase(likeProductQuestion.rejected, (state, action) => {
        const qnaId = normalizeId(action.meta.arg?.questionId);
        if (!qnaId) return;
        state.reactionStatusByQuestionId[qnaId] = "failed";
        state.reactionErrorByQuestionId[qnaId] =
          action.payload || action.error?.message || "Failed to like question";
      })
      .addCase(dislikeProductQuestion.pending, (state, action) => {
        const qnaId = normalizeId(action.meta.arg?.questionId);
        if (!qnaId) return;
        state.reactionStatusByQuestionId[qnaId] = "loading";
        state.reactionErrorByQuestionId[qnaId] = "";
      })
      .addCase(dislikeProductQuestion.fulfilled, (state, action) => {
        const qnaId = normalizeId(action.payload?.qnaId || action.payload?._id || action.meta.arg?.questionId);
        if (!qnaId) return;

        state.reactionStatusByQuestionId[qnaId] = "succeeded";
        state.reactionErrorByQuestionId[qnaId] = "";

        const likes = Number.isFinite(Number(action.payload?.likes))
          ? Number(action.payload.likes)
          : null;
        const dislikes = Number.isFinite(Number(action.payload?.dislikes))
          ? Number(action.payload.dislikes)
          : null;
        const helpfulCount = Number.isFinite(Number(action.payload?.helpfulCount))
          ? Number(action.payload.helpfulCount)
          : likes;

        Object.keys(state.listByProduct).forEach((productKey) => {
          state.listByProduct[productKey] = (state.listByProduct[productKey] || []).map((item) => {
            if (normalizeId(item?._id) !== qnaId) return item;
            return {
              ...item,
              likesCount: likes ?? item.likesCount ?? 0,
              dislikesCount: dislikes ?? item.dislikesCount ?? 0,
              helpfulCount: helpfulCount ?? item.helpfulCount ?? item.likesCount ?? 0,
            };
          });
        });
      })
      .addCase(dislikeProductQuestion.rejected, (state, action) => {
        const qnaId = normalizeId(action.meta.arg?.questionId);
        if (!qnaId) return;
        state.reactionStatusByQuestionId[qnaId] = "failed";
        state.reactionErrorByQuestionId[qnaId] =
          action.payload || action.error?.message || "Failed to dislike question";
      });
  },
});

export const { clearQnaErrors } = productQnaSlice.actions;

export default productQnaSlice.reducer;
